import type { ReactNode } from "react";

import { useTablistKeyboardNavigation } from "../TabSelector/useTablistKeyboardNavigation";

import styles from "./PillTabs.module.css";

/**
 * A single tab descriptor. `id` is the value passed back to `onChange`;
 * the generic parameter on `PillTabsProps` keeps this type-safe for
 * callers that use a string-literal union (e.g. `"kpis" | "list"`).
 */
export type PillTabsItem<T extends string = string> = {
  id: T;
  label: ReactNode;
  /** Disabled tabs are still rendered, just non-interactive. */
  disabled?: boolean;
  /** Optional per-tab test id suffix; falls back to `id` when omitted. */
  testId?: string;
  /**
   * Optional DOM `id` for the tab button — useful when a downstream
   * `<div role="tabpanel">` references it via `aria-labelledby`.
   */
  elementId?: string;
  /**
   * Optional `aria-controls` value linking the tab to the id of its
   * associated tabpanel. Only set for full WAI-ARIA compliance; otherwise
   * omit.
   */
  ariaControls?: string;
};

export type PillTabsProps<T extends string = string> = {
  items: ReadonlyArray<PillTabsItem<T>>;
  /** Controlled active tab id. */
  value: T;
  onChange: (next: T) => void;
  /** Accessible name for the tablist (announced by screen readers). */
  ariaLabel?: string;
  className?: string;
  /** Root data-testid; per-tab buttons get `${dataTestId}-${item.testId|item.id}`. */
  "data-testid"?: string;
};

/**
 * Bordered pill-strip tablist — the "Affiliate KPIs / Affiliate List"
 * style tabs used at the top of feature pages. Each tab renders as a
 * pill button; the active one gets a solid grey-750 fill inside a shared
 * bordered container.
 *
 * Controlled-only — the parent owns `value` and updates it via
 * `onChange`. No internal state so the same instance can be re-rendered
 * against URL params or wizard parent state.
 *
 * Keyboard support comes from `useTablistKeyboardNavigation` (shared
 * with `TabSelector` and `SegmentedControl`). Implements the WAI-ARIA
 * Authoring Practices "tablist" pattern with automatic activation:
 *   - `Tab` moves focus into / out of the tablist (only the active tab
 *     is in the tab sequence; inactive tabs have `tabIndex={-1}`).
 *   - `ArrowLeft` / `ArrowRight` move to the previous / next enabled
 *     tab, wrapping at the ends.
 *   - `Home` / `End` jump to the first / last enabled tab.
 *   - Disabled tabs are skipped over.
 */
function PillTabs<T extends string = string>({
  items,
  value,
  onChange,
  ariaLabel,
  className = "",
  "data-testid": dataTestId,
}: PillTabsProps<T>) {
  const { tabRefs, handleKeyDown } = useTablistKeyboardNavigation<T>({
    items,
    value,
    onChange,
  });

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`${styles.root}${className ? ` ${className}` : ""}`}
      data-testid={dataTestId}
    >
      {items.map((item, index) => {
        const isActive = item.id === value;
        const tabClassName = `${styles.tab}${isActive ? ` ${styles.tabActive}` : ""}`;
        const tabTestId = dataTestId
          ? `${dataTestId}-${item.testId ?? item.id}`
          : undefined;
        return (
          <button
            key={item.id}
            id={item.elementId}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-disabled={item.disabled || undefined}
            aria-controls={item.ariaControls}
            tabIndex={isActive ? 0 : -1}
            disabled={item.disabled}
            className={tabClassName}
            onClick={() => {
              if (item.disabled || isActive) return;
              onChange(item.id);
            }}
            onKeyDown={(event) => handleKeyDown(event, index)}
            data-testid={tabTestId}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

export default PillTabs;
